import { decodeAudioDataCompat } from "./browserCompatibility";
import { SITE_TUNING } from "./siteTuning";

type RandomTeamsSound = "bounce" | "latch";

const SOUND_PATHS: Record<RandomTeamsSound, string> = {
    bounce: "/audio/random-teams/bounce.mp3",
    latch: "/audio/random-teams/latch.mp3"
};

const SOUND_VOLUME: Record<RandomTeamsSound, number> = {
    bounce: SITE_TUNING.randomTeams.audio.bounceVolume,
    latch: SITE_TUNING.randomTeams.audio.latchVolume
};
const GLOBAL_SOUND_VOLUME_MULTIPLIER = SITE_TUNING.randomTeams.audio.globalVolumeMultiplier;
const MOBILE_SOUND_VOLUME_MULTIPLIER = SITE_TUNING.randomTeams.audio.mobileVolumeMultiplier;
const MOBILE_SOUND_SHORT_SIDE_MAX_PX = SITE_TUNING.randomTeams.audio.mobileVolumeShortSideMaxPx;

export class RandomTeamsAudio {
    private context: AudioContext | null = null;
    private readonly buffers = new Map<RandomTeamsSound, AudioBuffer>();
    private readonly loading = new Map<RandomTeamsSound, Promise<void>>();
    private disposed = false;
    private primed = false;
    private contextToken = 0;

    public async prepare(): Promise<void> {
        const context = await this.ensureContextReady();

        if (!context) {
            return;
        }

        await Promise.all(Object.keys(SOUND_PATHS).map((sound) => this.load(sound as RandomTeamsSound)));
    }

    public playBounce() {
        const playbackRate = 0.9 + Math.random() * 0.2;
        this.play("bounce", playbackRate);
    }

    public playLatch(playbackRate: number) {
        this.play("latch", playbackRate);
    }

    public dispose() {
        this.disposed = true;
        const context = this.context;
        this.resetContext(context, { clearBuffers: true });
        context?.close().catch(() => undefined);
    }

    private getContext(): AudioContext | null {
        if (this.disposed) {
            return null;
        }

        if (this.context?.state === "closed") {
            this.resetContext(this.context);
        }

        if (this.context) {
            return this.context;
        }

        const AudioContextConstructor =
            window.AudioContext ??
            (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext })
                .webkitAudioContext;

        if (!AudioContextConstructor) {
            return null;
        }

        const context = new AudioContextConstructor();
        this.context = context;
        this.contextToken += 1;
        return context;
    }

    private async ensureContextReady(): Promise<AudioContext | null> {
        const context = this.getContext();

        if (!context) {
            return null;
        }

        if (context.state !== "running") {
            await context.resume().catch(() => undefined);
        }

        if (context.state === "running") {
            this.primeContext(context);
        }

        if (context.state === "closed") {
            this.resetContext(context);
            return null;
        }

        return context;
    }

    private primeContext(context: AudioContext) {
        if (this.primed) {
            return;
        }

        const createBuffer = (context as AudioContext & { createBuffer?: AudioContext["createBuffer"] }).createBuffer;

        if (typeof createBuffer !== "function") {
            this.primed = true;
            return;
        }

        try {
            const buffer = createBuffer.call(context, 1, 1, Math.max(22050, context.sampleRate || 22050));
            const source = context.createBufferSource();
            const gain = context.createGain();

            gain.gain.value = 0;
            source.buffer = buffer;
            source.connect(gain);
            gain.connect(context.destination);
            source.start(0);
            source.onended = () => {
                source.disconnect();
                gain.disconnect();
            };
            this.primed = true;
        } catch {
            this.primed = true;
        }
    }

    private load(sound: RandomTeamsSound): Promise<void> {
        const existingBuffer = this.buffers.get(sound);

        if (existingBuffer) {
            return Promise.resolve();
        }

        const existingLoad = this.loading.get(sound);

        if (existingLoad) {
            return existingLoad;
        }

        const context = this.getContext();

        if (!context) {
            return Promise.resolve();
        }

        const contextToken = this.contextToken;
        let loadPromise: Promise<void>;
        loadPromise = fetch(SOUND_PATHS[sound])
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Unable to load ${sound} sound`);
                }

                return response.arrayBuffer();
            })
            .then((arrayBuffer) => {
                if (this.isContextStale(context, contextToken)) {
                    return null;
                }

                return decodeAudioDataCompat(context, arrayBuffer);
            })
            .then((buffer) => {
                if (!buffer || this.isContextStale(context, contextToken)) {
                    return;
                }

                this.buffers.set(sound, buffer);
            })
            .catch(() => undefined)
            .finally(() => {
                if (this.loading.get(sound) === loadPromise) {
                    this.loading.delete(sound);
                }
            });

        this.loading.set(sound, loadPromise);
        return loadPromise;
    }

    private play(sound: RandomTeamsSound, playbackRate: number) {
        void this.playWhenReady(sound, playbackRate);
    }

    private async playWhenReady(sound: RandomTeamsSound, playbackRate: number) {
        const context = await this.ensureContextReady();

        if (!context) {
            return;
        }

        if (!this.buffers.has(sound)) {
            await this.load(sound);
        }

        const buffer = this.buffers.get(sound);

        if (!buffer) {
            return;
        }

        if (context.state !== "running") {
            await context.resume().catch(() => undefined);
        }

        if (context.state !== "running" || this.disposed) {
            return;
        }

        const source = context.createBufferSource();
        const gain = context.createGain();

        source.buffer = buffer;
        source.playbackRate.value = Math.max(0.5, Math.min(playbackRate, 1.5));
        gain.gain.value = this.getOutputVolume(sound);
        source.connect(gain);
        gain.connect(context.destination);
        source.onended = () => {
            source.disconnect();
            gain.disconnect();
        };
        source.start();
    }

    // A backgrounded tab can lose its AudioContext. Keep decoded buffers in memory so
    // the replacement context can play immediately instead of reloading local SFX.
    private resetContext(context: AudioContext | null, options: { clearBuffers?: boolean } = {}) {
        if (context && this.context && context !== this.context) {
            return;
        }

        this.context = null;
        this.primed = false;
        this.loading.clear();

        if (options.clearBuffers) {
            this.buffers.clear();
        }

        this.contextToken += 1;
    }

    private isContextStale(context: AudioContext, contextToken: number): boolean {
        if (this.disposed || this.context !== context || this.contextToken !== contextToken) {
            return true;
        }

        if (context.state === "closed") {
            this.resetContext(context);
            return true;
        }

        return false;
    }

    private getOutputVolume(sound: RandomTeamsSound): number {
        const baseVolume = SOUND_VOLUME[sound] * GLOBAL_SOUND_VOLUME_MULTIPLIER;

        if (!this.isPhoneSizedTouchDevice()) {
            return baseVolume;
        }

        return baseVolume * MOBILE_SOUND_VOLUME_MULTIPLIER;
    }

    private isPhoneSizedTouchDevice(): boolean {
        const shortSide = Math.min(window.innerWidth || Number.POSITIVE_INFINITY, window.innerHeight || Number.POSITIVE_INFINITY);
        const hasTouchPrimaryInput =
            navigator.maxTouchPoints > 0 ||
            window.matchMedia("(pointer: coarse)").matches ||
            window.matchMedia("(any-pointer: coarse)").matches;

        return hasTouchPrimaryInput && shortSide <= MOBILE_SOUND_SHORT_SIDE_MAX_PX;
    }
}
